import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import '../pages/robot_control/components/reader';

export const AUTH_TYPE_NONE = 'none';
const AUTH_TYPE_BASIC = 'basic';
const AUTH_TYPE_BEARER = 'bearer';

const normalizeAuthType = (authType) => {
  const value = (authType || AUTH_TYPE_NONE).toLowerCase();
  if (value === AUTH_TYPE_BASIC || value === AUTH_TYPE_BEARER) {
    return value;
  }
  return AUTH_TYPE_NONE;
};

const shouldUseWebRTCReader = (url) => {
  if (!url) return false;

  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '').toLowerCase();
    const transport = (u.searchParams.get('transport') || '').toLowerCase();
    const mode = (u.searchParams.get('mode') || '').toLowerCase();

    return path.endsWith('/whep') || transport === 'webrtc' || mode === 'webrtc';
  } catch {
    return false;
  }
};

export const shouldUseWebRTCReaderForStream = (url, streamAuth) => {
  const authType = normalizeAuthType(streamAuth?.AuthType);
  if (authType === AUTH_TYPE_BASIC || authType === AUTH_TYPE_BEARER) {
    return true;
  }
  return shouldUseWebRTCReader(url);
};

const getWhepCandidates = (url) => {
  if (!url) return [];

  try {
    const u = new URL(url);
    u.search = '';
    u.hash = '';

    const basePath = u.pathname.replace(/\/+$/, '');
    const directUrl = new URL(u.toString());
    directUrl.pathname = basePath || '/';

    if (basePath.endsWith('/whep')) {
      return [directUrl.toString()];
    }

    const whepUrl = new URL(directUrl.toString());
    whepUrl.pathname = `${basePath}/whep`;

    // Try direct URL first for reverse-proxy setups, then explicit /whep.
    return [directUrl.toString(), whepUrl.toString()];
  } catch {
    return [];
  }
};

const parseStreamSource = (url) => {
  if (!url) {
    return { whepUrls: [], user: '', pass: '' };
  }

  try {
    const parsed = new URL(url);
    const user = parsed.username ? decodeURIComponent(parsed.username) : '';
    const pass = parsed.password ? decodeURIComponent(parsed.password) : '';

    // Credentials are provided explicitly to reader.js for basic auth.
    parsed.username = '';
    parsed.password = '';
    const normalized = parsed.toString();

    return {
      whepUrls: getWhepCandidates(normalized),
      user,
      pass,
    };
  } catch {
    return { whepUrls: [], user: '', pass: '' };
  }
};

const resolveBearerToken = async (streamAuth, fallbackToken, fetchBearerToken) => {
  const authUrl = streamAuth?.AuthUrl?.trim() || '';
  const clientId = streamAuth?.BearerClientId?.trim() || '';
  const hasExternalCredentials = Boolean(
    authUrl
    && clientId
    && streamAuth?.HasBearerClientSecret
  );

  if (!hasExternalCredentials) {
    return fallbackToken || '';
  }

  if (!streamAuth?.Id || typeof fetchBearerToken !== 'function') {
    throw new Error('External bearer authentication is not available');
  }

  const response = await fetchBearerToken(streamAuth.Id);
  const payload = response?.RobotResponse || response;
  if (payload?.AccessToken) {
    return payload.AccessToken;
  }

  throw new Error('Backend authentication response does not contain an access token');
};

export function WebRTCCameraPlayer({ url, token, title, muted = true, streamAuth, fetchBearerToken }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const bearerTokenFetcherRef = useRef(fetchBearerToken);
  const [error, setError] = useState('');

  bearerTokenFetcherRef.current = fetchBearerToken;

  useEffect(() => {
    const videoElement = videoRef.current;
    let cancelled = false;
    let hasTrack = false;

    const start = async () => {
      if (!url || !videoElement) return;

      const source = parseStreamSource(url);
      const whepUrls = source.whepUrls;
      if (!whepUrls.length) {
        setError('Invalid stream URL');
        return;
      }

      try {
        const ReaderClass = window.MediaMTXWebRTCReader;
        if (!ReaderClass) {
          setError('MediaMTX reader.js is not loaded');
          return;
        }
        if (cancelled) return;

        setError('');

        const authType = normalizeAuthType(streamAuth?.AuthType);
        const basicUser = streamAuth?.BasicAuthUsername?.trim() || source.user;
        const basicPass = (streamAuth?.BasicAuthPassword || source.pass || '').trim();

        let authAttempts = [{ user: '', pass: '', token: '' }];
        if (authType === AUTH_TYPE_BASIC) {
          authAttempts = [{ user: basicUser, pass: basicPass, token: '' }];
        }

        if (authType === AUTH_TYPE_BEARER) {
          const bearerToken = await resolveBearerToken(
            streamAuth,
            token,
            bearerTokenFetcherRef.current
          );
          authAttempts = bearerToken
            ? [{ user: '', pass: '', token: bearerToken }]
            : [{ user: '', pass: '', token: '' }];
        }
        let currentUrlAttempt = 0;
        let currentAttempt = 0;

        const openReader = () => {
          if (cancelled || currentUrlAttempt >= whepUrls.length || currentAttempt >= authAttempts.length) {
            return;
          }

          const whepUrl = whepUrls[currentUrlAttempt];
          const auth = authAttempts[currentAttempt];

          readerRef.current = new ReaderClass({
            url: whepUrl,
            user: auth.user,
            pass: auth.pass,
            token: auth.token,
            onError: (err) => {
              if (cancelled) return;

              const message = typeof err === 'string' ? err : 'Failed to play stream';
              const authFailed = /401|403|unauthorized|forbidden|bad status code/i.test(message);
              const canRetryAuth = !hasTrack && authFailed && currentAttempt < authAttempts.length - 1;
              const endpointFailed = /404|405|stream not found|bad status code/i.test(message);
              const canRetryEndpoint = !hasTrack && endpointFailed && currentUrlAttempt < whepUrls.length - 1;

              if (canRetryAuth) {
                if (readerRef.current) {
                  readerRef.current.close();
                  readerRef.current = null;
                }
                currentAttempt += 1;
                openReader();
                return;
              }

              if (canRetryEndpoint) {
                if (readerRef.current) {
                  readerRef.current.close();
                  readerRef.current = null;
                }
                currentUrlAttempt += 1;
                currentAttempt = 0;
                openReader();
                return;
              }

              setError(message);
            },
            onTrack: (evt) => {
              hasTrack = true;
              if (!cancelled && videoElement && evt.streams && evt.streams[0]) {
                setError('');
                videoElement.srcObject = evt.streams[0];
              }
            }
          });
        };

        openReader();
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Unable to initialize WebRTC player');
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (readerRef.current) {
        readerRef.current.close();
        readerRef.current = null;
      }
      if (videoElement) {
        videoElement.srcObject = null;
      }
    };
  }, [url, token, streamAuth]);

  if (!url) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '12px', textAlign: 'center', padding: '8px' }}>
        No stream URL
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', backgroundColor: '#000' }}>
      <Box
        component="video"
        ref={videoRef}
        title={title}
        autoPlay
        muted={muted}
        playsInline
        controls={false}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#000' }}
      />
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: '#f59e0b',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            fontSize: '11px',
            textAlign: 'center',
            padding: '10px',
            pointerEvents: 'none'
          }}
        >
          <Box sx={{ fontSize: '18px' }}>⏳</Box>
          <Box>Waiting for robot stream...</Box>
          <Box sx={{ color: '#999', fontSize: '10px' }}>{error}</Box>
        </Box>
      )}
    </Box>
  );
}

export const sanitizeUrl = (url) => {
  if (!url) return url;
  try {
    const urlObj = new URL(url);
    urlObj.username = '';
    urlObj.password = '';
    // Upgrade HTTP to HTTPS when page is served over HTTPS
    if (window.location.protocol === 'https:' && urlObj.protocol === 'http:') {
      urlObj.protocol = 'https:';
    }
    return urlObj.toString();
  } catch (error) {
    console.warn('Invalid URL:', url);
    return url;
  }
};

// Returns true when the URL would be blocked as mixed content
export const isMixedContent = (url) => {
  if (!url) return false;
  try {
    return window.location.protocol === 'https:' && new URL(url).protocol === 'http:';
  } catch {
    return false;
  }
};
